<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useZendeskMessenger } from '@base-shared/composables/use-zendesk-messenger';
import IconSvg from '@base-shared/components/IconSvg.vue';
import { openLink } from '@base-shared/utils';

const props = defineProps<{
  enabled: boolean;
  languageCode?: string;
}>();

const trackingExceptionLink = 'https://support.microsoft.com/en-us/microsoft-edge/learn-about-tracking-prevention-in-microsoft-edge-5ac125e8-9b90-8d59-fa2c-7f2e9a44d869';

const showLauncher = ref(props.enabled);
const chatCouldNotBeLoaded = ref(false);
const liveChatLauncher = ref<HTMLElement | null>(null);
const zendeskMessenger = useZendeskMessenger();

async function openWidget() {
  const status = await zendeskMessenger.checkZendeskStatus();

  // If chat cannot be loaded, show the notification
  if (status.isBlocked) {
    chatCouldNotBeLoaded.value = true;
    showLauncher.value = false;
    return;
  }

  zendeskMessenger.toggleVisibility(true);
  zendeskMessenger.openMessenger();
  if (liveChatLauncher.value) {
    liveChatLauncher.value.classList.remove('animate-slide-in-up');
    liveChatLauncher.value.classList.add('animate-slide-out-down');
  }
}

function closeWidget() {
  zendeskMessenger.toggleVisibility(false);
  // Add delay to allow the close animation to finish
  setTimeout(() => {
    if (liveChatLauncher.value) {
      liveChatLauncher.value.classList.remove('animate-slide-out-down');
      liveChatLauncher.value.classList.add('animate-slide-in-up');
    }
  }, 1000);
}

function hideNotification() {
  // Hide the notification, and show the launcher again
  chatCouldNotBeLoaded.value = false;
  showLauncher.value = true;
}

watch(() => props.languageCode, () => {
  zendeskMessenger.setLocale(props.languageCode ?? 'en');
});

onMounted(async () => {
  const isZendeskReady = await zendeskMessenger.isZendeskReady();

  if (!isZendeskReady) {
    console.warn('Zendesk Messenger failed to initialize.');
    return;
  }

  zendeskMessenger.setLocale(props.languageCode ?? 'en');
  zendeskMessenger.toggleVisibility(false);
  zendeskMessenger.setCloseEvent(() => closeWidget());
});
</script>

<template>
  <div>
    <div
      v-if="showLauncher"
      id="liveChatLauncher"
      ref="liveChatLauncher"
      class="opacity-100 overflow-visible fixed bottom-0 right-0 z-999"
      @click="openWidget()"
    >
      <button class="live-chat-btn items-center justify-center flex border-0 rounded-full bg-[#7464BC] cursor-pointer h-11 w-11 m-3.5">
        <IconSvg
          class="w-5 h-5"
          name="logo:live-chat-icon"
        />
      </button>
    </div>
    <div
      v-if="chatCouldNotBeLoaded"
      class="absolute bottom-4 right-4 bg-white w-[523px] flex shadow-lg p-4 rounded-lg"
    >
      <div class="bg-[#7464BC] rounded-full h-11 w-11 flex items-center p-3.5 justify-center mr-3">
        <IconSvg
          class="w-5! h-5!"
          name="logo:live-chat-icon"
        />
      </div>
      <div class="text-left">
        <div class="font-semibold text-sm">
          <span class="text-gray-900">
            {{ $t('zendesk.chatCouldNotBeLoaded') }}
          </span>
          <button
            class="absolute top-0 right-0 m-3 border-none bg-transparent"
            @click="hideNotification"
          >
            <IconSvg
              class="w-4! h-4! inline-block ml-2 cursor-pointer text-gray-400"
              name="flowbite:close-outline"
            />
          </button>
        </div>
        <i18n-t
          keypath="zendesk.chatCouldNotBeLoadedDescription"
          tag="p"
          class="text-gray-600 text-sm"
        >
          <template #url>
            <a
              class="text-primary-600 inline-flex items-center hover:underline hover:cursor-pointer"
              data-component-element-id="enable-support-chat-instructions-link"
              @click="openLink(trackingExceptionLink, true)"
            >
              (URL)
            </a>
          </template>
        </i18n-t>
      </div>
    </div>
  </div>
</template>
